/**
 * Videos vor dem Hochladen verkleinern.
 *
 * Dieselbe Stelle wie bei den Fotos (`images.ts`) und beim Ton
 * (`tonwandeln.ts`): **der Server kann es nicht** — kein ffmpeg, kein
 * Composer —, also rechnet der Browser, der die Datei ohnehin in der Hand hat.
 *
 * Aus dem Betrieb: „damit man nicht auf Einstellungen der Foto-App im Export
 * angewiesen ist." Zwei Dinge kostet ein unangetastetes Video: **Platz** (4K
 * mit 60 Bildern wiegt ein Vielfaches) und **Reichweite** (HEVC spielt in
 * Chrome und Firefox nicht ab).
 *
 * **Der Weg:** `mp4box` zerlegt die Datei, `VideoDecoder` dekodiert,
 * gezeichnet wird auf die Zielgröße, `VideoEncoder` kodiert nach H.264,
 * `mp4-muxer` schreibt. Beide Pakete werden **erst beim ersten Video
 * nachgeladen**; wer nie eines schickt, lädt sie nie.
 *
 * **Der Ton wird durchgereicht, nicht neu gerechnet.** Seine Stücke gehen so,
 * wie sie in der Datei stehen, in die neue — kein Qualitätsverlust und keine
 * zweite Stelle, an der etwas stumm werden kann.
 *
 * **Ein Video hat kein „durchspielen" nötig.** Der erste Entwurf holte die
 * Bilder aus einem `<video>`-Element; das läuft in Spieldauer, weil ein
 * Element eben abspielt. Über den Demuxer läuft es so schnell, wie das Gerät
 * kodiert.
 *
 * **Was nicht geht, geht gar nicht hinaus** (#41). Fehlt WebCodecs, kann das
 * Gerät den Eingang nicht dekodieren oder bricht etwas mittendrin, dann steht
 * hier `kompatibel: false` samt Grund — und `uploadVideo()` bricht ab.
 *
 * **Hier stand das Gegenteil**, und es war als Freundlichkeit gemeint: „ein
 * Reisebericht soll nicht daran scheitern, dass eine Nebenrechnung nicht
 * durchkam." Aus dem Betrieb kam die andere Sicht, und sie ist die richtige:
 * das Umrechnen ist keine Nebenrechnung, sondern der Grund, warum ein Video
 * bei allen ankommt. Ein Beitrag, den die Hälfte der Mitlesenden nicht
 * abspielen kann, ist kein geretteter Beitrag.
 */

import type { Aufnahme } from './exif'
import { groesse } from './format'
import { notiere } from './sendeprotokoll'
import { instantOf1904, stampMp4 } from './mp4Stamp'
import { readRecordingStamp } from './videoOrt'
import { WORTLAUT } from '../texte'

/**
 * Wohin kodiert wird, in dieser Reihenfolge.
 *
 * H.264 und nichts anderes: der Sinn der Übung ist, dass das Video **überall**
 * läuft. `4d0028` ist Main/Level 4 (bis 1080p), `42001f` Baseline als
 * Rückfall für ältere Geräte. The level in these names is the lowest one
 * asked for; `withLevel()` raises it to what the frame needs.
 */
export const ZIELKODIERUNGEN = ['avc1.4d0028', 'avc1.42001f'] as const

/**
 * H.264 levels with their limits (ITU-T H.264, Table A-1): macroblocks per
 * frame, macroblocks per second, and the bitrate in kbit/s for Baseline and
 * Main. The byte is the last two hex digits of an `avc1` name.
 */
const AVC_LEVELS: readonly { byte: number; frame: number; second: number; kbit: number }[] = [
  { byte: 0x1f, frame: 3600, second: 108000, kbit: 14000 },
  { byte: 0x20, frame: 5120, second: 216000, kbit: 20000 },
  { byte: 0x28, frame: 8192, second: 245760, kbit: 20000 },
  { byte: 0x29, frame: 8192, second: 245760, kbit: 50000 },
  { byte: 0x2a, frame: 8704, second: 522240, kbit: 50000 },
  { byte: 0x32, frame: 22080, second: 589824, kbit: 135000 },
  { byte: 0x33, frame: 36864, second: 983040, kbit: 240000 },
  { byte: 0x34, frame: 36864, second: 2073600, kbit: 240000 },
]

/**
 * The `avc1` name with a level that carries this frame (1.157.1).
 *
 * From the field: a screen recording from the iPad (2752 x 2064, 4:3)
 * failed on the iPhone with "Encoding task failed", and the diagnosis said
 * `avc1.4d0028 1920x1440`. Level 4 takes 8192 macroblocks per frame, and
 * 1920 x 1440 has 10800: every 4:3 or 3:4 video at full size broke, and
 * so would every 4K film. `isConfigSupported()` said yes to it; the encoder
 * failed only at the first frame. 1920 x 1080 has 8160 and fitted by 32.
 *
 * The level only ever goes up from the one in the name, never down: the
 * name says the least a caller wants. A name that is not `avc1`/`avc3`, or
 * a frame no level carries, comes back unchanged.
 */
export function withLevel(codec: string, width: number, height: number, fps: number, bitrate?: number): string {
  const match = /^(avc[13]\.[0-9a-f]{4})([0-9a-f]{2})$/i.exec(codec)
  if (match === null) return codec
  const prefix = match[1] ?? ''
  const base = parseInt(match[2] ?? '0', 16)
  const frame = Math.ceil(width / 16) * Math.ceil(height / 16)
  const second = frame * Math.max(1, fps)
  const kbit = bitrate === undefined ? 0 : bitrate / 1000
  const level = AVC_LEVELS.find((l) => l.byte >= base && l.frame >= frame && l.second >= second && l.kbit >= kbit)
  if (level === undefined) return codec
  return prefix + level.byte.toString(16).padStart(2, '0')
}

/**
 * Bis hierher lohnt das Umrechnen nicht.
 *
 * Dieselbe Regel wie `KEEP_ORIGINAL_BYTES` bei den Fotos, und aus demselben
 * Grund: neu rechnen kostet immer etwas. Ein kurzer Clip, der schon klein ist,
 * gewinnt nichts und verliert Bildqualität.
 */
const KLEIN_GENUG_BYTES = 25 * 1024 * 1024

/**
 * Welcher Teil des Fortschritts aufs Lesen entfaellt.
 *
 * Eine Schaetzung und keine Messung -- gemessen wuerde sie erst, wenn beides
 * durch ist, und dann braucht sie niemand mehr. Rechnen dauert um ein
 * Vielfaches laenger als lesen; ein Zehntel ist die Groessenordnung.
 */
const LESEANTEIL = 0.1

/**
 * Wie viele Bilder hoechstens angesehen werden, um ein Standbild zu finden.
 *
 * Dreissig sind bei dreissig Bildern je Sekunde die erste Sekunde -- lange
 * genug, dass eine Kamera fertig belichtet hat, kurz genug, dass es im
 * Rechenlauf nicht auffaellt. Neun Bildpunkte je Versuch kosten nichts.
 */
const STANDBILD_VERSUCHE = 30

/**
 * Den Fortschritt drosseln.
 *
 * **Warum das noetig ist.** Gemeldet wurde je Bild -- bei einer Aufnahme mit
 * 2743 Bildern also 2743 Mal. Am anderen Ende haengt in beiden Aufrufern ein
 * Zustand: in der Eingabezeile `setFotos(alt => alt.map(...))`, im Formular am
 * Punkt ein `setFortschritt`. Das sind zweitausendsiebenhundert
 * Zustandsaenderungen samt Neuzeichnen -- **auf demselben Faden, der gerade
 * dekodiert, skaliert und kodiert.**
 *
 * **Gedrosselt wird hier und nicht bei den Aufrufern.** Zwei Kopien
 * derselben Regel laufen auseinander; das ist in dieser Sache schon einmal
 * passiert (Standbild, #4).
 *
 * Durchgelassen wird, was einen halben Prozentpunkt weiter ist **oder** nach
 * hundert Millisekunden -- und die 1 immer, denn ein Balken, der bei 99
 * stehen bleibt, sieht aus wie ein Fehler. Sichtbar aendert sich sonst
 * nichts: ein Balken, der zweitausendmal je Sekunde wackelt, ist keine
 * Auskunft, sondern Flimmern.
 */
export function gedrosselt(
  melde: ((anteil: number) => void) | undefined,
  jetzt: () => number = () => Date.now(),
): (anteil: number) => void {
  if (melde === undefined) return () => undefined

  let zuletztWert = -1
  let zuletztZeit = -Infinity
  return (anteil) => {
    const fertig = anteil >= 1
    if (!fertig && anteil - zuletztWert < 0.005 && jetzt() - zuletztZeit < 100) return
    zuletztWert = anteil
    zuletztZeit = jetzt()
    melde(anteil)
  }
}

/**
 * Wie viel auf einmal von der Platte in den Leser geht.
 *
 * Ein Megabyte: `appendBuffer` mit zweihundert Megabyte am Stueck legt ein
 * Telefon lahm, und kleine Stuecke kosten nichts -- zweihundert Durchlaeufe
 * merkt niemand. Nebenbei meldet der Fortschritt damit oft genug etwas.
 */
export const LESE_STUECK = 1024 * 1024

export interface Wandelziel {
  /** Längste Kante in Bildpunkten — aus den Einstellungen (`video_max_edge`). */
  maxEdge: number
  /** Höchste Bildrate — aus den Einstellungen (`video_max_fps`). */
  maxFps: number
  /**
   * Womit kodiert werden darf.
   *
   * **Diese Tür gibt es für den Prüflauf**, und das gehört gesagt: das
   * Chromium in der Prüfumgebung kann H.264 überhaupt nicht — weder kodieren
   * noch dekodieren. Ohne die Möglichkeit, eine andere Kodierung vorzugeben,
   * ließe sich die ganze Mechanik (Zerlegen, Zeichnen, Auslassen, Drehen,
   * Schreiben) nirgends messen. Im Betrieb wird sie nicht gesetzt, und dann
   * gilt `ZIELKODIERUNGEN`.
   */
  kodierungen?: readonly string[]
  /**
   * Welchen Weg das Bild zum Kodierer nimmt — `direkt` oder über die
   * `leinwand`.
   *
   * **Diese Tür gibt es für den Prüflauf**, wie `kodierungen`, und aus einem
   * handfesten Grund: normalerweise wird die Frage gestellt
   * (`skaliertSelbst()`), und auf jedem Gerät fällt die Antwort gleich aus.
   * Damit liefe der jeweils andere Zweig **nirgends** — und das ist der
   * Zweig, der einspringt, wenn ein Kodierer eben *nicht* selbst skaliert.
   * Ein Rückfall, den nichts je durchläuft, ist keiner.
   *
   * Im Betrieb wird sie nicht gesetzt, und dann entscheidet die Messung.
   */
  skalieren?: 'direkt' | 'leinwand'
  /**
   * Wie der Kodierer rechnen soll — `quality` oder `realtime`.
   *
   * Aus den Einstellungen (`video_latency`). **Am iPhone gemessen bringt
   * `realtime` keine Zeit und kostet die halbe Datenrate** — derselbe Film in
   * 29,9 s statt 29,8 s, aber 37 MB statt 73 MB. Die Begründung steht bei der
   * Einstellung; hier zählt nur, dass die Angabe ankommt.
   *
   * Fehlt sie, gilt `quality` — genau das, was hier vorher fest stand.
   */
  latenz?: 'quality' | 'realtime'
  /**
   * The step of the data rate, from the settings (`video_bitrate`, 1.166.0).
   * Missing, it is `standard` -- what stood here before.
   */
  datenrate?: Datenratenstufe
}

export interface Wandelergebnis {
  /** Was hochgeladen wird — verkleinert oder das Original. */
  datei: File
  verkleinert: boolean
  /** Warum nicht verkleinert wurde. Leer, wenn es geklappt hat. */
  grund: string
  vorher: number
  nachher: number
  /**
   * Die Maße des Ergebnisses — `0`, wenn nichts gerechnet wurde.
   *
   * Damit die Oberfläche sagen kann, **was** herausgekommen ist. „Es wurde
   * verkleinert" ohne Zahl ist eine Behauptung; wer sie nachprüfen will,
   * müsste die Datei suchen.
   */
  breite: number
  hoehe: number
  /**
   * Wie lange das Video läuft, in Millisekunden — `0`, wenn unbekannt.
   *
   * **Steht hier, damit niemand ein `<video>`-Element danach fragen muss.**
   * Genau das hat den Upload zum Hängen gebracht (#4): das Element wartete
   * nach dem letzten Stück auf ein `loadedmetadata`, das auf manchen Geräten
   * nie kam. Der Zerleger weiß es ohnehin — aus `duration` und `timescale`
   * der Spur.
   */
  dauerMs: number
  /**
   * Die Kodierung der Quelle und die des Ergebnisses.
   *
   * **Für die Kachel** (#4, „bei Videos … auch noch das Format darstellen"),
   * und sie beantwortet nebenbei die Frage, warum dort 360p steht: ist
   * `quellkodierung` schon H.264 und wurde nichts gerechnet, dann war die
   * Aufnahme selbst so klein. Leer heißt: nicht gelesen.
   */
  quellkodierung: string
  zielkodierung: string
  /**
   * Das Standbild, beim Umrechnen abgefallen — `null`, wenn es keins gibt.
   *
   * **Der kurze Weg zum Vorschaubild** (#4). Wurde gerechnet, lag jedes Bild
   * ohnehin auf der Leinwand; eines davon liegt hier. Wurde nichts gerechnet
   * (`schon-gut`), steht hier `null`, und `uploadVideo()` geht den alten Weg
   * über ein `<video>`-Element — das sind kleine Dateien, dort hat es nie
   * geklemmt.
   */
  standbild: Blob | null
  /**
   * Läuft die Datei, die hier herauskommt, **überall** (#41)?
   *
   * Aus dem Betrieb: „egal welches Ausgangsformat das Bild/Video hat (HEVC,
   * HEIC, …) → es landet IMMER in einem kompatiblen Format (iOS, Windows,
   * Android)."
   *
   * `true` heißt: frisch nach H.264 gerechnet, oder die Quelle war es schon.
   * `false` heißt: hier liegt das Original, und es ist keins von beiden --
   * dann darf es **nicht** hochgeladen werden, und `grund` sagt warum. Die
   * Entscheidung darüber trifft `uploadVideo()`; hier steht nur der Befund.
   */
  kompatibel: boolean
}

/**
 * Ist auf dieser Fläche nichts als Schwarz? Stichprobe über neun Punkte.
 *
 * **Hier, weil es zwei Stellen braucht** (#4): das Standbild aus dem
 * Umrechnen prüft eine `OffscreenCanvas`, `standbild()` in `video.ts` eine
 * gewöhnliche. Zweimal dieselbe Stichprobe wäre genau das, was auseinander
 * läuft — und `video.ts` holt sich ohnehin schon von hier, umgekehrt wäre es
 * ein Kreis.
 *
 * **Geprüft wird auf schwarz, nicht auf „überall dieselbe Farbe":** ein Bild
 * darf einfarbig sein. Eine weiße Wand, Nebel, ein Nachthimmel — alles
 * gültige Standbilder, und sie wegzuwerfen wäre schlimmer als der Fehler, den
 * diese Prüfung sucht.
 */
export function schwarz(
  context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  breite: number,
  hoehe: number,
): boolean {
  const punkte = [0.2, 0.5, 0.8]

  for (const x of punkte) {
    for (const y of punkte) {
      const [r, g, b] = context.getImageData(
        Math.floor(breite * x),
        Math.floor(hoehe * y),
        1,
        1,
      ).data
      // Nicht auf exakt 0 prüfen: ein dunkles Bild aus der Dämmerung liegt
      // knapp darüber, eine leere Leinwand nicht.
      if ((r ?? 0) + (g ?? 0) + (b ?? 0) > 24) return false
    }
  }
  return true
}

/**
 * Die kurze Kante — das ist die Zahl, die „1080p" meint.
 *
 * Eine Hochkantaufnahme misst 1080 x 1920; „1920p" stünde dort und wäre
 * falsch. Gemeint ist immer die kleinere der beiden Kanten.
 */
export function kurzeKante(masse: { breite: number; hoehe: number }): number {
  if (masse.breite <= 0 || masse.hoehe <= 0) return 0
  return Math.min(masse.breite, masse.hoehe)
}

/**
 * Ein Kürzel, wie ein Mensch es liest: `avc1.4d0028` → `H.264`.
 *
 * Vier Zeichen aus einem Boxbaum sagen dem Betrachter nichts. Bleibt es
 * unbekannt, steht das Kürzel selbst da — geraten wird nicht.
 */
export function formatName(kodierung: string): string {
  switch (familie(kodierung)) {
    case 'avc': return 'H.264'
    case 'hevc': return 'HEVC'
    case 'vp9': return 'VP9'
    case 'av1': return 'AV1'
    default: return kodierung === '' ? '' : kodierung
  }
}

/**
 * Was aus dem Video wurde, als Satz: „33 MB → 2,4 MB · 1080p · HEVC → H.264".
 *
 * **An einer Stelle**, weil er an zweien steht — in der Eingabezeile des Chats
 * und im Formular am Highlight. Zwei Kopien desselben Satzes laufen
 * auseinander, sobald eine von beiden etwas dazubekommt.
 *
 * **Auch wenn nichts gerechnet wurde, steht hier etwas** (#4). Vorher gab es
 * an dieser Stelle ein `return null`, und genau deshalb sah man einer
 * unveränderten Aufnahme nichts an — die Meldung aus dem Betrieb lautete
 * „360p steht in der Kachel, die Einstellung ist 1080", und die Auflösung war
 * die einzige Zahl, die den Fall erklärt hätte. Sie war die der **Quelle**:
 * hochgerechnet wird nie.
 */
export function ausbeuteText(befund: Wandelergebnis): string | null {
  const kante = kurzeKante(befund)
  const von = formatName(befund.quellkodierung)
  const nach = formatName(befund.zielkodierung)

  const teile: string[] = []
  teile.push(
    befund.verkleinert
      ? `${groesse(befund.vorher)} → ${groesse(befund.nachher)}`
      : groesse(befund.nachher),
  )
  if (kante > 0) teile.push(`${kante}p`)
  // Zweimal dasselbe Format ist kein Pfeil wert.
  if (von !== '' && nach !== '' && von !== nach) teile.push(`${von} → ${nach}`)
  else if (nach !== '') teile.push(nach)
  else if (von !== '') teile.push(von)

  // Nur die Dateigröße wäre keine Auskunft -- die steht ohnehin schon da.
  return teile.length > 1 ? teile.join(' · ') : null
}

/** Kann dieser Browser überhaupt umrechnen? */
/**
 * Ein Paket nachladen -- **und sagen, was fehlt, wenn es nicht kommt.**
 *
 * `mp4box` und `mp4-muxer` werden erst beim ersten Video geholt (der Grund
 * steht oben). Kommt der Nachschlag nicht, wirft der Browser „Importing a
 * module script failed" -- und dieser Satz landete bis 1.45.0 in der Klammer
 * von `nichtUmgerechnetMitGrund()`, also in einem Satz, der dem **Gerät** die
 * Schuld gab. Aus dem Betrieb, mit Bildschirmfoto, und er stimmte nicht: das
 * Gerät konnte es, der Download war nicht da.
 *
 * Zwei Lagen, in denen das eintritt, und beide gehen vorbei: ohne Netz, und im
 * Augenblick eines FTP-Uploads, in dem die Datei gerade ersetzt wird -- eine
 * fehlende Datei beantwortet der Front-Controller mit der Hülle und Status
 * 200, und HTML ist kein Skript. Deshalb „versuch es noch einmal" und nicht
 * „dieses Gerät kann es nicht".
 *
 * **Die Absage aus #41 bleibt**: hochgeladen wird trotzdem nichts. Ein Video,
 * das die Hälfte nicht abspielen kann, ist der schlechtere Ausgang.
 */
export async function nachladen<T>(laden: () => Promise<T>): Promise<T> {
  try {
    return await laden()
  } catch {
    throw new Error(WORTLAUT.technik.video.nachschlagFehlt)
  }
}

export function kannWandeln(): boolean {
  return (
    typeof VideoEncoder !== 'undefined'
    && typeof VideoDecoder !== 'undefined'
    && typeof VideoFrame !== 'undefined'
    && typeof OffscreenCanvas !== 'undefined'
  )
}

/**
 * Whether a video is refused before it is touched: too large **and** this
 * device cannot make it smaller (1.101.0, review). A device that converts
 * brings any video under the limit, so the size alone says nothing there.
 * The chat and the point's form asked it this way; the map's "photos onto
 * the map" asked only the size and refused a video the chat took.
 */
export function videoTooLarge(file: File, maxMb: number | null | undefined): boolean {
  return !kannWandeln() && maxMb !== null && maxMb !== undefined && file.size > maxMb * 1024 * 1024
}

/**
 * Ein Video auf Zielgröße und Zielbildrate bringen.
 *
 * Wirft nicht: was schiefgeht, kommt als `grund` zurück, und die Datei ist
 * dann das Original. Nur der Abbruch durch den Benutzer wird durchgereicht —
 * der ist kein Fehler, sondern eine Ansage.
 */
export async function verkleinereVideo(
  file: File,
  ziel: Wandelziel,
  onFortschritt?: (anteil: number) => void,
  signal?: { abgebrochen: boolean },
): Promise<Wandelergebnis> {
  /**
   * Das Original bleibt liegen.
   *
   * **Mit den Angaben der Quelle, wenn es welche gibt.** Vorher stand hier
   * `breite: 0, hoehe: 0` und kein Format -- und damit sah man einer
   * unveraenderten Aufnahme nichts an. Genau das war die Meldung „360p steht
   * in der Kachel" (#4): die Zahl fehlte an der einzigen Stelle, an der sie
   * den Fall erklaert haette. Vor dem Zerlegen ist noch nichts bekannt, dann
   * bleibt es bei den Nullen.
   */
  const unveraendert = (
    grund: string,
    kompatibel: boolean,
    // **Mit Standbild, wenn eines abgefallen ist.** Auch ein Video, das
    // unveraendert hinausgeht, hat inzwischen eines -- geholt aus der Quelle
    // statt aus einem `<video>`-Element (#4).
    quelle?: Quellangaben & { standbild?: Blob | null },
  ): Wandelergebnis => ({
    datei: file,
    verkleinert: false,
    grund,
    vorher: file.size,
    nachher: file.size,
    breite: quelle?.quellbreite ?? 0,
    hoehe: quelle?.quellhoehe ?? 0,
    dauerMs: quelle?.dauerMs ?? 0,
    quellkodierung: quelle?.quelle ?? '',
    // Was liegen bleibt, ist selbst das Ergebnis.
    zielkodierung: quelle?.quelle ?? '',
    standbild: quelle?.standbild ?? null,
    kompatibel,
  })

  if (!kannWandeln()) {
    notiere('Video nicht umgerechnet: dieser Browser kennt WebCodecs nicht')
    return unveraendert(WORTLAUT.technik.video.browserKannNicht, false)
  }

  /*
   * **Ein zweiter Anlauf, weil ein zweiter Anlauf hilft** (#4).
   *
   * Aus dem Betrieb, und es ist die entscheidende Beobachtung: „es geht auch
   * ohne Schliessen -- einfach noch einmal probieren." Damit ist „Decoder
   * failure" **kein Zustand**, der sich ansammelt, sondern ein
   * voruebergehender Fehlschlag: das Geraet kann in diesem Augenblick keinen
   * Dekodierer hergeben, kurz darauf schon.
   *
   * Wenn ein Mensch das von Hand tut und es dann geht, gehoert es der
   * Anwendung. Ein rotes Kaestchen, das beim zweiten Antippen verschwindet,
   * ist keine Auskunft, sondern eine Zumutung.
   *
   * **Zweierlei gehoert dazu.** Erstens eine Pause: sofort noch einmal zu
   * fragen heisst, dieselbe Antwort zu bekommen -- das Geraet braucht den
   * Augenblick, um wieder herzugeben, was es gerade genommen hat. Zweitens
   * ein frischer Lauf: `wandle()` faengt bei der Datei an und liest sie neu.
   * Das muss es auch, denn die Bildstuecke des ersten Laufs sind unterwegs
   * losgelassen worden.
   *
   * **Und ein Abbruch wird nicht wiederholt.** Wer auf „abbrechen" tippt,
   * meint es.
   */
  const ANLAEUFE = 2
  const PAUSE_MS = 2000

  const angefangen = Date.now()
  for (let anlauf = 1; ; anlauf += 1) {
    try {
      return await einAnlauf()
    } catch (fehler) {
      if (signal?.abgebrochen) throw fehler
      const grund = fehler instanceof Error ? fehler.message : WORTLAUT.technik.video.nichtUmgerechnet
      if (anlauf >= ANLAEUFE) {
        // **Was hier ankommt, ist ungewandelt und keine H.264-Datei** -- ein
        // Browser ohne Dekoder, ein Format, das das Gerät nicht liest, ein
        // Abbruch mittendrin. Der Befund geht mit hinaus; hochgeladen wird es
        // nicht (#41).
        // „1 Anläufe" stand in der Gegenprobe da und liest sich falsch --
        // zwei Formen reichen, das Deutsche kennt Einzahl und Mehrzahl.
        notiere(
          `Video nicht umgerechnet: ${grund}`
          + ` (nach ${anlauf === 1 ? 'einem Anlauf' : `${anlauf} Anläufen`})`,
        )
        return unveraendert(grund, false)
      }
      notiere(`Video-Anlauf ${anlauf} fehlgeschlagen: ${grund} — neuer Versuch in ${PAUSE_MS / 1000}s`)
      await new Promise((weiter) => setTimeout(weiter, PAUSE_MS))
    }
  }

  async function einAnlauf(): Promise<Wandelergebnis> {
    const gewandelt = await wandle(file, ziel, onFortschritt, signal)
    // Nichts zu tun: die Quelle war schon H.264 und klein genug.
    if (gewandelt.art === 'schon-gut') return unveraendert('', true, gewandelt)

    // **Größer als vorher ist kein Gewinn -- außer, es geht um mehr als
    // Größe** (#41).
    //
    // Hier stand die Regel ohne Bedingung, und darin steckte das eigentliche
    // Loch: von HEVC nach H.264 wird eine Datei regelmäßig *größer*, weil
    // H.264 für dasselbe Bild mehr Bits braucht. Dann blieb das HEVC-Original
    // liegen -- und genau das spielt in Chrome und Firefox nicht ab. Die
    // Größe hat also gegen die Abspielbarkeit gewonnen, und das ist verkehrt
    // herum.
    //
    // Das Original behalten darf man nur, wenn es **selbst schon** überall
    // läuft; dann ging es wirklich nur um Platz, und das größere Ergebnis
    // wäre ein schlechter Handel.
    // **Die Aufteilung gehoert daneben, nicht in eine eigene Zeile.** Wer die
    // Gesamtzeit zweier Laeufe vergleicht, will die Teile im selben Blick --
    // sonst rechnet er sie aus zwei Zeilen zusammen und vertut sich.
    const sek = (ms: number): string => `${Math.round(ms / 100) / 10}s`
    const ganz = Date.now() - angefangen
    notiere(
      `Video gerechnet: ${groesse(file.size)} → ${groesse(gewandelt.blob.size)}`
      + ` in ${sek(ganz)}`
      + ` (zeichnen ${sek(gewandelt.aufteilung.zeichnen)}`
      + ` · warten ${sek(gewandelt.aufteilung.warten)}`
      + ` in ${gewandelt.aufteilung.rueckstaus} Takten`
      // **Ungeklemmt.** Hier stand `Math.max(0, …)`, und genau das hat den
      // Widerspruch zugedeckt: die beiden Teile waren zusammen groesser als
      // das Ganze, und „rest 0s" sah trotzdem plausibel aus. Eine Klammer,
      // die eine unmoegliche Zahl huebsch macht, ist keine Diagnose.
      + ` · rest ${sek(ganz - gewandelt.aufteilung.zeichnen - gewandelt.aufteilung.warten)})`,
    )

    if (gewandelt.blob.size >= file.size && familie(gewandelt.quelle) === 'avc') {
      notiere('Video bleibt, wie es ist: gerechnet waere es groesser, und die Quelle ist schon H.264')
      return unveraendert('', true, gewandelt)
    }
    return {
      datei: new File([gewandelt.blob], neuerName(file.name), { type: 'video/mp4' }),
      verkleinert: gewandelt.blob.size < file.size,
      grund: '',
      vorher: file.size,
      nachher: gewandelt.blob.size,
      breite: gewandelt.breite,
      hoehe: gewandelt.hoehe,
      dauerMs: gewandelt.dauerMs,
      standbild: gewandelt.standbild,
      quellkodierung: gewandelt.quelle,
      // Gerechnet wird immer nach H.264 -- die Kandidatenliste kennt nichts
      // anderes, und `uploadVideo()` laesst nichts anderes hinaus (#41).
      zielkodierung: 'avc1',
      kompatibel: true,
    }
  }
}

/** `IMG_1234.MOV` → `IMG_1234.mp4` — geschrieben wird immer MP4. */
function neuerName(name: string): string {
  return name.replace(/\.[^.]+$/, '') + '.mp4'
}

/** Was aus der Datei gelesen wurde. */
interface Zerlegt {
  breite: number
  hoehe: number
  drehung: 0 | 90 | 180 | 270
  kodierung: string
  beschreibung: Uint8Array | undefined
  zeitbasis: number
  /** Wie lange die Bildspur laeuft, in Millisekunden. */
  dauerMs: number
  /** Wie viele Bilder die Verwaltungsdaten ankuendigen -- fuer die Diagnose. */
  angekuendigt: number
  bilder: Bildstueck[]
  ton: Tonspur | null
}

interface Bildstueck {
  daten: Uint8Array
  schluessel: boolean
  /** Mikrosekunden. */
  zeit: number
  dauer: number
}

interface Tonspur {
  kodierung: 'aac' | 'opus'
  rate: number
  kanaele: number
  beschreibung: Uint8Array | undefined
  stuecke: Bildstueck[]
}

/**
 * Der eigentliche Durchlauf. `null` heisst: es lohnt nicht, lass das Original.
 */
/**
 * Was `wandle()` zurückgibt.
 *
 * **Zwei Fälle und kein `null`.** Hier stand `… | null`, und `null` hieß
 * „nichts zu tun, die Quelle war schon gut". Das ließ sich von „ging nicht"
 * nicht unterscheiden -- und seit #41 hängt genau daran, ob hochgeladen
 * werden darf. Die Kodierung der Quelle geht mit: ohne sie kann der Aufrufer
 * nicht entscheiden, ob das Original selbst schon überall läuft.
 */
interface Quellangaben {
  /** Die Kodierung der Quelle -- das Vierzeichen-Kuerzel aus `stsd`. */
  quelle: string
  /** Die Masse der Quelle. */
  quellbreite: number
  quellhoehe: number
  /** Wie lange sie laeuft, in Millisekunden. */
  dauerMs: number
}

/**
 * **Die Quellangaben stehen in beiden Faellen.** Vorher trug `schon-gut` gar
 * nichts, und deshalb wusste der Aufrufer von einer unveraenderten Aufnahme
 * weder Format noch Groesse noch Dauer -- die Kachel blieb stumm, und
 * `uploadVideo()` musste die Masse hinterher aus einem `<video>`-Element
 * holen. Genau dort hing es (#4).
 */
type Wandelung =
  // **Auch dieser Ausgang traegt ein Standbild.** Vorher tat er es nicht, und
  // fuer ein Video direkt aus der Kamera gab es deshalb gar keins: klein
  // genug, schon H.264, nichts gerechnet -- und der Rueckfall auf ein
  // `<video>`-Element schwieg (#4).
  | ({ art: 'schon-gut'; standbild: Blob | null } & Quellangaben)
  | ({
      art: 'gewandelt'
      blob: Blob
      breite: number
      hoehe: number
      /** Beim Rechnen abgefallen -- `null`, wenn alles Angesehene schwarz war. */
      standbild: Blob | null
      /** Wo die Zeit hinging, in Millisekunden -- fuer den Mitschrieb. */
      aufteilung: { zeichnen: number; warten: number; rueckstaus: number }
    } & Quellangaben)

/** Was von der Quelle nach draussen geht -- in beiden Ausgaengen dasselbe. */
function angaben(quelle: Zerlegt): Quellangaben {
  return {
    quelle: quelle.kodierung,
    // **Wie es angesehen wird**, nicht wie es in der Datei liegt: bei einer
    // Hochkantaufnahme liegen die Bilder quer und die Drehmarke steht daneben.
    quellbreite: quelle.drehung === 90 || quelle.drehung === 270 ? quelle.hoehe : quelle.breite,
    quellhoehe: quelle.drehung === 90 || quelle.drehung === 270 ? quelle.breite : quelle.hoehe,
    dauerMs: quelle.dauerMs,
  }
}

async function wandle(
  file: File,
  ziel: Wandelziel,
  onFortschritt?: (anteil: number) => void,
  signal?: { abgebrochen: boolean },
): Promise<Wandelung> {
  // **Das Lesen zaehlt mit.** Zweihundert Megabyte von der Platte zu holen und
  // zu zerlegen dauert bei einem grossen Video spuerbar; ohne diesen Anteil
  // stuende der Balken erst eine Weile still und finge dann bei null an --
  // was aussieht, als sei nichts passiert.
  // **Eine Drossel fuer beide Abschnitte**, Lesen wie Rechnen: sonst haette
  // jeder seine eigene, und der Uebergang zwischen ihnen ruckelte.
  const melde = gedrosselt(onFortschritt)

  const quelle = await zerlege(file, signal, (anteil) => melde(anteil * LESEANTEIL))
  if (quelle.bilder.length === 0) {
    // Es **gibt** eine Bildspur, sie kam nur nicht an. Die Zahl daneben ist
    // die Auskunft, an der man das erkennt.
    throw new Error(`Die Bildspur kam nicht an (0 von ${quelle.angekuendigt} Bildern).`)
  }

  const bilderJeSekundeQuelle = rate(quelle.bilder)
  notiere(
    `Video gelesen: ${quelle.kodierung} ${quelle.breite}x${quelle.hoehe}`
    + `${quelle.drehung === 0 ? '' : ` gedreht ${quelle.drehung}°`}`
    + ` · ${Math.round(bilderJeSekundeQuelle)}/s · ${Math.round(quelle.dauerMs / 100) / 10}s`
    + ` · ${quelle.bilder.length} von ${quelle.angekuendigt} Bildern`,
  )

  const lang = Math.max(quelle.breite, quelle.hoehe)
  const faktor = Math.min(1, ziel.maxEdge / lang)
  // H.264 will gerade Kantenlängen; eine ungerade legt manchen Kodierer lahm.
  const breite = gerade(Math.round(quelle.breite * faktor))
  const hoehe = gerade(Math.round(quelle.hoehe * faktor))

  const kandidaten = ziel.kodierungen ?? ZIELKODIERUNGEN
  const bilderJeSekunde = rate(quelle.bilder)
  // **Verglichen wird mit dem Ziel, nicht mit einem Namen im Code.** Hier
  // stand `startsWith('avc1')` -- dieselbe Regel ein zweites Mal
  // hingeschrieben, und schon der erste Prueflauf hat sie auseinanderlaufen
  // sehen.
  const schonKlein =
    file.size <= KLEIN_GENUG_BYTES
    && faktor === 1
    && bilderJeSekunde <= ziel.maxFps + 1
    && familie(quelle.kodierung) === familie(kandidaten[0] ?? '')
  if (schonKlein) {
    notiere(`Video bleibt, wie es ist: schon ${quelle.kodierung}, klein genug, ${lang} ≤ ${ziel.maxEdge}`)
    return { art: 'schon-gut', standbild: await standbildAusQuelle(quelle, signal), ...angaben(quelle) }
  }

  // The rate goes into the probe and into `configure()`: the level depends
  // on it (1.157.1), and the rule at `ersteMoegliche()` asks with the values
  // the encoder is set to.
  const zielFps = Math.min(ziel.maxFps, bilderJeSekunde)
  const stufe = ziel.datenrate ?? 'standard'
  const wahl = await ersteMoegliche(kandidaten, breite, hoehe, zielFps, stufe)
  if (wahl === null) {
    // **Kein Kodierer heisst nicht zwangsläufig „geht nicht"** (#41).
    //
    // Aus dem Prüflauf: dieses Gerät hat keinen H.264-Kodierer, und damit
    // wurde auch ein Video abgelehnt, das **schon** H.264 war und nur nicht
    // verkleinert werden konnte. Das ist zu streng: gefordert ist, dass die
    // Datei überall abspielt, nicht dass sie klein ist. Ist die Quelle schon
    // die richtige Familie, geht sie unverändert hinaus -- der Platz ist der
    // Preis, und er ist der kleinere.
    if (familie(quelle.kodierung) === 'avc') {
      notiere(`Video bleibt, wie es ist: kein H.264-Kodierer, Quelle ist aber ${quelle.kodierung}`)
      return { art: 'schon-gut', standbild: await standbildAusQuelle(quelle, signal), ...angaben(quelle) }
    }
    notiere(`Video abgelehnt: kein Kodierer für ${kandidaten.join(', ')}`)
    throw new Error(WORTLAUT.technik.video.keinH264)
  }

  const kodierung = wahl.codec
  const datenrateZiel = datenrate(breite, hoehe, zielFps, stufe)
  const direkt = ziel.skalieren === undefined
    ? await skaliertSelbst(
        kodierung,
        wahl.hardware,
        { breite: quelle.breite, hoehe: quelle.hoehe },
        { breite, hoehe },
        datenrateZiel,
      )
    : ziel.skalieren === 'direkt'
  notiere(
    `Video-Ziel: ${kodierung} ${breite}x${hoehe}`
    + ` · ${Math.min(ziel.maxFps, Math.round(bilderJeSekunde))}/s`
    + ` · ${Math.round(datenrateZiel / 1000)} kbit/s`
    + ` (lange Kante hoechstens ${ziel.maxEdge}, ${ziel.latenz ?? 'quality'}, Datenrate ${stufe},`
    + ` ${wahl.hardware === 'prefer-hardware' ? 'Hardware' : 'Software'}`
    // **Welcher Weg genommen wurde, gehoert daneben.** Ohne die Angabe waere
    // ein langsamer Lauf nicht von einem schnellen zu unterscheiden, und
    // genau darum geht es hier.
    + `, ${direkt ? 'direkt' : 'Leinwand'})`,
  )

  const leseweg = await leseWeg(quelle)
  const lesekonfig = leseweg?.konfig
  const lesehardware = leseweg?.hardware ?? null
  if (leseweg === undefined || lesekonfig === undefined) {
    // Der Name der Kodierung gehoert dazu: „das Format" sagt niemandem, ob es
    // an HEVC lag oder an etwas anderem.
    throw new Error(`Dieses Gerät kann ${quelle.kodierung} nicht lesen.`)
  }

  const { Muxer, ArrayBufferTarget } = await nachladen(() => import('mp4-muxer'))
  // **Der Schreiber kennt vier Familien, „unbekannt" ist keine.** Die
  // Zielkodierung stammt aus unserer eigenen Liste; steht dort etwas, das er
  // nicht kennt, ist das ein Fehler im Code und keine Lage, die ein Video
  // herbeiführen kann. Deshalb hier eine Ansage und kein stiller Rückfall.
  const zielFamilie = familie(kodierung)
  if (zielFamilie === 'unbekannt') {
    throw new Error(`Unbekannte Zielkodierung: ${kodierung}`)
  }

  const schreiber = new Muxer({
    target: new ArrayBufferTarget(),
    // `in-memory` legt die Verwaltungsdaten an den **Anfang** der Datei. Sonst
    // holt sich ein Player erst das Ende, bevor er etwas zeigen kann -- eine
    // Anfrage mehr über Mobilfunk, und genau darum ging es in #3.
    fastStart: 'in-memory',
    video: {
      // **Nicht fest `avc`.** Die Familie folgt der gewaehlten Kodierung --
      // sonst stuende im Kopf der Datei etwas anderes, als in ihr steht, und
      // kein Player koennte sie oeffnen.
      codec: zielFamilie,
      width: breite,
      height: hoehe,
      rotation: quelle.drehung,
      // **Kein `frameRate`.** Der Schreiber rundet damit alle Zeitstempel auf
      // dieses Raster -- und ein iPhone nimmt mit 29,97 oder 59,94 Bildern
      // auf, nicht mit 30 oder 60. Nach dem Auslassen liegen die Zeitstempel
      // also **neben** dem Raster, und zwei benachbarte Bilder koennten auf
      // denselben Wert fallen. Ein Bild ohne Dauer bringt manchen Player aus
      // dem Tritt. Die Zeitstempel aus der Quelle sagen die Wahrheit; eine
      // Rundung darueber gewinnt nichts.
    },
    ...(quelle.ton === null
      ? {}
      : {
          audio: {
            codec: quelle.ton.kodierung,
            numberOfChannels: quelle.ton.kanaele,
            sampleRate: quelle.ton.rate,
          },
        }),
  })

  let fehlgeschlagen: Error | null = null

  /**
   * Beide Werkzeuge weglegen -- und zwar auf **jedem** Weg hier heraus (#4).
   *
   * **Hier war das Leck.** Geschlossen wurde nur beim Abbruch und am Ende des
   * Regelfalls; jeder Wurf dazwischen liess Dekodierer und Kodierer offen.
   * Das sind Hardwarebausteine, und ein iPhone hat wenige davon. Aus dem
   * Betrieb kam die Signatur dazu: dasselbe Video, das um 21:29 in 84
   * Sekunden durchlief, scheiterte um 21:33 mit „Decoder failure". Ein
   * Fehlschlag zog damit alle folgenden mit -- aus einem Ausrutscher wurde
   * ein Zustand, den nur ein Neuladen der Seite aufhob.
   *
   * `close()` wirft, wenn schon geschlossen wurde; deshalb einmal fragen und
   * beide Schritte einzeln kapseln.
   */
  const weglegen = (): void => {
    try {
      if (dekodierer.state !== 'closed') dekodierer.close()
    } catch {
      // Schon zu. Nichts zu tun.
    }
    try {
      if (kodierer.state !== 'closed') kodierer.close()
    } catch {
      // Ebenso.
    }
  }

  const kodierer = new VideoEncoder({
    output: (stueck, meta) => schreiber.addVideoChunk(stueck, meta),
    error: (fehler) => {
      fehlgeschlagen = fehler instanceof Error ? fehler : new Error(String(fehler))
    },
  })
  kodierer.configure({
    codec: kodierung,
    width: breite,
    height: hoehe,
    // **Genau das, wonach vorher gefragt wurde.** `ersteMoegliche()` hat
    // beides zusammen ermittelt; hier noch einmal eigenmaechtig etwas
    // hinzuzuschreiben hiesse, dieselbe Regel ein zweites Mal aufzustellen --
    // und diese eine laesst den Kodierer gar nicht erst entstehen, wenn sie
    // nicht erfuellt ist.
    hardwareAcceleration: wahl.hardware,
    bitrate: datenrateZiel,
    framerate: zielFps,
    // Eine Bildgruppe je zwei Sekunden: kurz genug, dass Vorspulen nicht
    // hakt, lang genug, dass die Schlüsselbilder nicht die halbe Datei sind.
    latencyMode: ziel.latenz ?? 'quality',
  })

  // **Ohne Alphakanal.** Ein Videobild ist undurchsichtig; mit `alpha` rechnet
  // der Browser bei jedem `drawImage` eine Durchsichtigkeit mit, die es nicht
  // gibt. Das ist der billigste Gewinn im heissen Pfad.
  const leinwand = new OffscreenCanvas(breite, hoehe)
  const stift = leinwand.getContext('2d', { alpha: false })
  if (stift === null) throw new Error(WORTLAUT.technik.video.keineLeinwand)

  /*
   * **Das Standbild faellt hier ab** (#4).
   *
   * Aus dem Betrieb, dreimal gemessen und zweimal reproduziert: auf einem
   * iPhone meldet ein `<video>`-Element **fuer eine frisch gerechnete Datei
   * ueberhaupt nicht** -- auch nicht in zwanzig Sekunden, und auch nicht bei
   * kleiner Last: gemessen an einer 16-Sekunden-Umrechnung mit 11 MB Ergebnis -- auf einem iPad schon.
   * Der Weg ueber ein zweites Element dekodiert die Datei **noch einmal**,
   * mit eigenem Dekodierer, auf einem Geraet, das gerade zwei Minuten
   * gerechnet hat.
   *
   * Dabei liegt das Bild hier vor: `leinwand` traegt jedes einzelne, um es
   * neu zu kodieren. Eines mitzunehmen kostet einen `drawImage`.
   *
   * **Kopiert wird auf eine zweite Flaeche, und zwar synchron.** Die erste
   * ist beim naechsten Bild ueberschrieben, und `convertToBlob()` ist
   * asynchron -- wer sie erst spaeter fragt, bekommt ein anderes Bild.
   */
  // **Dieselbe Mechanik wie fuer ein Video, das gar nicht gerechnet wird** --
  // und mit ihr die Drehung, die hier vorher fehlte: zu einer
  // Hochkantaufnahme gehoerte ein querformatiges Vorschaubild.
  const fang = standbildfang(breite, hoehe, quelle.drehung)

  /*
   * **Wo die Zeit hingeht** (#4).
   *
   * Aus dem Betrieb kam die Frage, ob sich das Rechnen beschleunigen laesst --
   * rund dreissig Bilder je Sekunde am iPhone, also etwa Spieldauer. Bevor
   * daran gedreht wird, gehoert gemessen, **woran** es liegt; sonst
   * optimiert man die Stelle, die gar nicht schuld ist. Genau das ist in
   * dieser Sache schon dreimal passiert.
   *
   * Zwei Zahlen reichen, um die Richtung zu entscheiden:
   *
   * - **zeichnen** -- der Umweg ueber die 2D-Leinwand, also `drawImage` und
   *   das Aufnehmen der Leinwand in ein neues Bild. Beides laeuft synchron
   *   und ist damit direkt zu messen. Ist das gross, lohnt der Versuch, den
   *   Kodierer selbst skalieren zu lassen.
   * - **warten** -- die Zeit im Rueckstau. Ist das gross, sind Dekodierer
   *   oder Kodierer der Engpass und nicht wir; dann hilft nur der Kodierer
   *   selbst.
   */
  let zeitZeichnen = 0
  let zeitWarten = 0
  let rueckstaus = 0

  // **Der kleinste Abstand zwischen zwei behaltenen Bildern.** Ausgelassen
  // wird beim Kodieren, nie beim Dekodieren: ein Zwischenbild hängt an seinen
  // Vorgängern, wer es überspringt, bekommt Schlieren.
  const abstand = 1_000_000 / ziel.maxFps
  let zuletzt = Number.NEGATIVE_INFINITY
  let gezeichnet = 0

  const dekodierer = new VideoDecoder({
    output: (bild) => {
      try {
        if (bild.timestamp - zuletzt + 1 < abstand) return
        zuletzt = bild.timestamp
        const vorZeichnen = performance.now()

        /*
         * **Der erste Augenblick ist oft schwarz**, weil die Kamera noch
         * belichtet -- aus demselben Grund spulte der alte Weg auf 0,1 s vor.
         * Deshalb wird weiter ueberschrieben, solange nur Schwarz dasteht,
         * hoechstens ueber `STANDBILD_VERSUCHE` Bilder. Bleibt es schwarz,
         * gibt es kein Standbild: ein schwarzes Rechteck ist schlechter als
         * keins, dafuer hat der Player einen Ausweg.
         *
         * **Das ist jetzt die einzige Stelle, an der ueberhaupt gezeichnet
         * wird**, wenn der Kodierer selbst skaliert -- und zwar hoechstens
         * dreissig Mal statt dreitausend.
         */
        fang?.nimm(bild)

        // Alle zwei Sekunden ein Schlüsselbild -- siehe oben.
        const schluesselbild = gezeichnet % Math.max(1, Math.round(ziel.maxFps * 2)) === 0
        if (direkt) {
          /*
           * **Der kurze Weg**: das dekodierte Bild geht, wie es ist, in den
           * Kodierer, und der bringt es auf seine eingestellte Groesse. Dass
           * er das tut, ist vorher an einem einzigen Bild gemessen worden
           * (`skaliertSelbst`) -- angenommen wird es nicht.
           *
           * `encode()` nimmt sich, was es braucht; das `close()` im `finally`
           * darunter darf unmittelbar folgen.
           */
          zeitZeichnen += performance.now() - vorZeichnen
          kodierer.encode(bild, { keyFrame: schluesselbild })
        } else {
          stift.drawImage(bild, 0, 0, breite, hoehe)
          const neu = new VideoFrame(leinwand, {
            timestamp: bild.timestamp,
            duration: bild.duration ?? undefined,
          })
          zeitZeichnen += performance.now() - vorZeichnen
          kodierer.encode(neu, { keyFrame: schluesselbild })
          neu.close()
        }
        gezeichnet += 1
      } finally {
        bild.close()
      }
    },
    error: (fehler) => {
      fehlgeschlagen = fehler instanceof Error ? fehler : new Error(String(fehler))
    },
  })
  dekodierer.configure({
    ...lesekonfig,
    // Dieselbe Ansage wie beim Kodierer, und aus demselben Grund dieselbe wie
    // die, mit der gefragt wurde.
    hardwareAcceleration: lesehardware ?? 'no-preference',
  })

  /*
   * **Ab hier gilt `finally`** (#4). Vorher stand `close()` an zwei Stellen --
   * beim Abbruch und am guten Ende --, und jeder andere Weg hier heraus liess
   * beide offen: der Wurf bei `fehlgeschlagen`, der Wurf aus dem Rueckstau,
   * ein abgelehntes `flush()`, ein Fehler beim Zeichnen. Genau die Wege, die
   * ein Geraet unter Last nimmt.
   */
  try {
    for (let i = 0; i < quelle.bilder.length; i += 1) {
      if (signal?.abgebrochen) throw new Error(WORTLAUT.technik.video.umrechnenAbgebrochen)
      if (fehlgeschlagen !== null) throw fehlgeschlagen

      const stueck = quelle.bilder[i]
      if (stueck === undefined) continue
      dekodierer.decode(
        new EncodedVideoChunk({
          type: stueck.schluessel ? 'key' : 'delta',
          timestamp: stueck.zeit,
          duration: stueck.dauer,
          data: stueck.daten,
        }),
      )
      // **Hier stand ein `stueck.daten = new Uint8Array(0)`** -- die Stuecke
      // sollten waehrend des Rechnens losgelassen werden, um Speicher zu
      // sparen. Es ist wieder heraus, und der Grund gehoert aufgehoben:
      //
      // Der Nutzen war eine **Vermutung** -- mp4box haelt die Bytes
      // vermutlich ohnehin in einem grossen Puffer, dann gibt das Loslassen
      // der einzelnen Sicht gar nichts frei. Das Risiko war dagegen
      // **beobachtet**: es war die einzige neue Beruehrung der Daten in genau
      // der Version, in der aus dem Betrieb „Decoder failure" gemeldet wurde.
      // Ein gemessener Befund schlaegt eine Vermutung.

      // **Rückstau.** Ein 4K-Bild wiegt zwölf Megabyte; ohne Bremse stünden
      // hundert davon gleichzeitig im Speicher, und der Reiter stürzte ab.
      const vorWarten = performance.now()
      // **Das Zeichnen passiert *hier drin*.** Der Rueckruf des Dekodierers
      // laeuft, waehrend diese Schleife wartet -- ein `await` ist genau die
      // Gelegenheit, bei der er drankommt. Ohne den Abzug zaehlte dieselbe
      // Zeit zweimal, und die Zeile im Mitschrieb ergab mehr als hundert
      // Prozent: „in 155.3s (zeichnen 126.5s · warten 132.7s)". Aus dem
      // Betrieb, und es war genau die Zahl, die die naechste Runde
      // entscheiden sollte.
      const gezeichnetVorher = zeitZeichnen
      while (dekodierer.decodeQueueSize > 8 || kodierer.encodeQueueSize > 8) {
        rueckstaus += 1
        await neuerTakt()
        if (fehlgeschlagen !== null) throw fehlgeschlagen
      }
      zeitWarten += performance.now() - vorWarten - (zeitZeichnen - gezeichnetVorher)
      melde(LESEANTEIL + ((i + 1) / quelle.bilder.length) * (1 - LESEANTEIL))
    }

    await dekodierer.flush()
    await kodierer.flush()
    if (fehlgeschlagen !== null) throw fehlgeschlagen
  } finally {
    weglegen()
  }

  if (quelle.ton !== null) {
    const beschreibung = quelle.ton.beschreibung
    for (const stueck of quelle.ton.stuecke) {
      schreiber.addAudioChunkRaw(
        stueck.daten,
        stueck.schluessel ? 'key' : 'delta',
        stueck.zeit,
        stueck.dauer,
        beschreibung === undefined
          ? undefined
          : {
              decoderConfig: {
                codec: quelle.ton.kodierung === 'aac' ? 'mp4a.40.2' : 'opus',
                sampleRate: quelle.ton.rate,
                numberOfChannels: quelle.ton.kanaele,
                description: beschreibung,
              },
            },
      )
    }
  }

  schreiber.finalize()
  const fertig = await mitAufnahme(file, new Uint8Array(schreiber.target.buffer))
  return {
    art: 'gewandelt',
    // Wofür sie gebraucht wird, steht bei „Größer als vorher": ob das
    // Original selbst schon überall läuft.
    ...angaben(quelle),
    // **Erst jetzt in einen Blob** -- vorher haette es das Rechnen aufgehalten.
    standbild: fang?.sitzt() === true ? await fang.blob() : null,
    aufteilung: { zeichnen: zeitZeichnen, warten: zeitWarten, rueckstaus },
    blob: new Blob([fertig], { type: 'video/mp4' }),
    // **Die Maße, wie das Video angesehen wird.** Bei einer Aufnahme hochkant
    // liegen die Bilder quer in der Datei und die Drehmarke steht daneben --
    // wer hier die Zahlen des Kodierers nennt, sagt „1920 x 1080" zu etwas,
    // das jeder hochkant sieht.
    breite: quelle.drehung === 90 || quelle.drehung === 270 ? hoehe : breite,
    hoehe: quelle.drehung === 90 || quelle.drehung === 270 ? breite : hoehe,
  }
}

/**
 * Recording time and place into the new file (#115, 1.158.0).
 *
 * Here and not in `uploadVideo()`: this is the one place where a new MP4 is
 * born from a recording, and both are at hand. A failure costs the stamp and
 * is noted; the video goes out as the muxer wrote it.
 */
async function mitAufnahme(quelle: Blob, geschrieben: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  try {
    const stamp = await readRecordingStamp(quelle)
    const fertig = stampMp4(geschrieben, stamp)
    if (fertig === null && stamp.created === null && stamp.iso6709 === null) {
      notiere('Video ohne Aufnahmestempel: Quelle ohne Zeit und Ort')
    } else if (fertig === null) {
      notiere('Video ohne Aufnahmestempel: Datei nicht gestempelt')
    } else {
      notiere(`Video gestempelt: ${stamp.created === null ? 'ohne Zeit' : instantOf1904(stamp.created)}`
        + ` · ${stamp.iso6709 ?? 'ohne Ort'}`
        + (stamp.placeRaw === undefined ? '' : ` · aus „${stamp.placeRaw}“`))
    }
    return fertig ?? geschrieben
  } catch (fehler) {
    notiere(`Video ohne Aufnahmestempel: ${fehler instanceof Error ? fehler.message : String(fehler)}`)
    return geschrieben
  }
}

/** Nächste gerade Zahl nach unten, mindestens 2. */
function gerade(wert: number): number {
  return Math.max(2, wert - (wert % 2))
}

/**
 * Bits per pixel and frame for the three steps of `video_bitrate` (1.166.0).
 *
 * 0.08 is the rate that stood here alone, and it is the order H.264 needs
 * for moving scenes: 1080p at 30 frames lands at about 5 Mbit/s. From use,
 * with uploaded videos: "es sind immer noch Komprimierungsartefakte". 1080p
 * at 60 frames is 10, 19 or 31 Mbit/s, about 75, 140 or 233 MB a minute.
 * Steps and not a number in kbit/s: edge and frame rate anyone can judge,
 * a rate nobody can. The route film keeps `standard` (`routeFilm.ts`).
 */
export const DATENRATEN = { standard: 0.08, high: 0.15, very_high: 0.25 } as const
export type Datenratenstufe = keyof typeof DATENRATEN

/**
 * The data rate, computed from the frame and one of the steps above. One
 * place for the probe, the level (`withLevel()`) and `configure()`: above
 * 20 Mbit/s a 1080p video needs level 4.1, and a probe with another rate
 * than the encoder's said yes where the first frame failed (1.157.1).
 */
export function datenrate(breite: number, hoehe: number, fps: number, stufe: Datenratenstufe = 'standard'): number {
  return Math.round(breite * hoehe * Math.max(1, fps) * DATENRATEN[stufe])
}

/** Bilder je Sekunde, aus den Zeitstempeln der Stücke. */
function rate(bilder: Bildstueck[]): number {
  const erstes = bilder[0]
  const letztes = bilder[bilder.length - 1]
  if (erstes === undefined || letztes === undefined || bilder.length < 2) return 30
  const spanne = letztes.zeit - erstes.zeit
  if (spanne <= 0) return 30
  return ((bilder.length - 1) * 1_000_000) / spanne
}

/**
 * Aus `avc1.4d0028` wird `avc` — die Familie, die der Schreiber kennt.
 */
/**
 * Welche Kodierungsfamilie -- und **„unbekannt" ist eine eigene Antwort**.
 *
 * Hier stand `return 'avc'` als Rest, und das war eine stille Zusage: eine
 * Kodierung, die niemand kennt, galt damit als H.264 und wurde vom
 * Überspringen mitgenommen. Genau das darf nach #41 nicht sein -- was nicht
 * nachweislich H.264 ist, wird gerechnet.
 */
export function familie(codec: string): 'avc' | 'hevc' | 'vp9' | 'av1' | 'unbekannt' {
  if (codec.startsWith('avc1') || codec.startsWith('avc3')) return 'avc'
  if (codec.startsWith('hvc1') || codec.startsWith('hev1')) return 'hevc'
  if (codec.startsWith('vp09')) return 'vp9'
  if (codec.startsWith('av01')) return 'av1'
  return 'unbekannt'
}

/**
 * Was der Kodierer kann -- Kodierung **und** Rechenweg in einem Stück.
 *
 * **`prefer-hardware` ist keine Bitte, sondern eine Bedingung** -- und das
 * hat der Prüflauf gefunden, bevor es jemand am Gerät gemerkt hätte. Der
 * Name liest sich wie „nimm gern die Hardware, sonst eben nicht"; in Chromium
 * meldet `isConfigSupported()` damit `false`, wenn keine da ist, und
 * `configure()` bricht mit „Encoder creation error" ab. Auf einem Rechner
 * ohne H.264-Baustein -- viele Linux- und Windows-Kisten, manches Android --
 * wäre damit **jede** Umrechnung fehlgeschlagen, zweimal hintereinander, und
 * das Video danach gar nicht hinausgegangen (#41).
 *
 * Deshalb wird gefragt, **wie konfiguriert wird**: dieselbe Angabe in
 * derselben Form. Erst die Runde mit Hardware über alle Kandidaten, dann die
 * ohne -- die Beschleunigung wiegt schwerer als das Profil, beide Kandidaten
 * sind ohnehin H.264.
 */
export interface Kodiererwahl {
  codec: string
  hardware: 'prefer-hardware' | 'no-preference'
}

export async function ersteMoegliche(
  kandidaten: readonly string[],
  breite: number,
  hoehe: number,
  fps?: number,
  stufe: Datenratenstufe = 'standard',
): Promise<Kodiererwahl | null> {
  const wege: Kodiererwahl['hardware'][] = ['prefer-hardware', 'no-preference']
  for (const hardware of wege) {
    for (const named of kandidaten) {
      // Without a known rate the upload's ceiling (`VIDEO_MAX_FPS`) is the
      // safe guess: a level too high costs nothing, one too low the video.
      const codec = withLevel(named, breite, hoehe, fps ?? 30, fps === undefined ? undefined : datenrate(breite, hoehe, fps, stufe))
      try {
        // **Asked with the frame rate and the bitrate the film is encoded
        // with** (1.112.0, review), where the caller knows them: Level 4
        // takes 1920 x 1080 at 30 frames and not at 60, and a probe
        // without the rate said yes where `configure()` then failed --
        // with a different error than "kein Kodierer", so the still was
        // not taken. The rule above (ask with the same values) holds for
        // every value that is set.
        const befund = await VideoEncoder.isConfigSupported({
          codec,
          width: breite,
          height: hoehe,
          hardwareAcceleration: hardware,
          ...(fps === undefined ? {} : { framerate: fps, bitrate: datenrate(breite, hoehe, fps, stufe) }),
        })
        if (befund.supported === true) return { codec, hardware }
      } catch {
        // Ein unbekannter Name wirft statt `false` zu melden. Nächster.
      }
    }
  }
  return null
}

/**
 * Skaliert dieser Kodierer selbst — oder braucht er die Leinwand?
 *
 * **Der Umweg über die 2D-Leinwand ist der teuerste Posten**, und das ist
 * gemessen und nicht geraten: bei 3489 Bildern aus einer 4K-Aufnahme
 * `zeichnen 126.5s` von `155.3s` insgesamt. Zwei Dinge stecken darin, und
 * beide fallen weg, wenn der Kodierer das dekodierte Bild unmittelbar
 * bekommt: das Verkleinern beim `drawImage` und das Zurückholen der Leinwand
 * in ein `VideoFrame` — acht Megabyte je Bild, dreitausendmal.
 *
 * **Gefragt wird, nicht angenommen.** WebCodecs sagt, ein Kodierer solle ein
 * Bild auf seine eingestellte Größe bringen; ob er es *tut*, steht damit noch
 * nicht fest, und ein Video in 4K statt in 1080p wäre der schlechteste
 * Ausgang von allen — es ginge groß hinaus, und niemand sähe warum. Also ein
 * einziges Bild zur Probe, in Quellgröße, durch einen eigenen Kodierer in
 * Zielgröße. Was herauskommt, sagt es.
 *
 * Verglichen wird großzügig: H.264 rundet die Bildhöhe auf Vielfache von
 * sechzehn auf (1080 wird zu 1088). Gefragt ist nicht die genaue Zahl,
 * sondern welcher der beiden Größen sie näher liegt.
 */
async function skaliertSelbst(
  kodierung: string,
  hardware: 'prefer-hardware' | 'no-preference',
  quelle: { breite: number; hoehe: number },
  ziel: { breite: number; hoehe: number },
  bitrate: number,
): Promise<boolean> {
  // Nichts zu skalieren: dann ist der Umweg ohnehin keiner.
  if (quelle.breite === ziel.breite && quelle.hoehe === ziel.hoehe) return true

  let gemessen: number | null = null
  let heil = true
  const probe = new VideoEncoder({
    output: (_stueck, meta) => {
      const breite = meta?.decoderConfig?.codedWidth
      if (typeof breite === 'number' && gemessen === null) gemessen = breite
    },
    error: () => {
      heil = false
    },
  })
  try {
    probe.configure({
      codec: kodierung,
      width: ziel.breite,
      height: ziel.hoehe,
      hardwareAcceleration: hardware,
      bitrate,
    })
    const flaeche = new OffscreenCanvas(quelle.breite, quelle.hoehe)
    const stift = flaeche.getContext('2d', { alpha: false })
    if (stift === null) return false
    // Kein Schwarz: manche Kodierer geben fuer eine leere Flaeche gar kein
    // Stueck heraus, und dann stuende hier „weiss nicht" statt einer Antwort.
    stift.fillStyle = '#7f7f7f'
    stift.fillRect(0, 0, quelle.breite, quelle.hoehe)
    const bild = new VideoFrame(flaeche, { timestamp: 0, duration: 1000 })
    probe.encode(bild, { keyFrame: true })
    bild.close()
    await probe.flush()
  } catch {
    // Wirft die Probe, gilt die Antwort „nein" -- der Umweg ist der sichere.
    return false
  } finally {
    if (probe.state !== 'closed') probe.close()
  }
  if (!heil || gemessen === null) return false
  return gemessen < (ziel.breite + quelle.breite) / 2
}

/**
 * Wie dieses Gerät die Quelle liest — Konfiguration **und** Rechenweg.
 *
 * Gefragt wird mit derselben Angabe, mit der konfiguriert wird; die
 * Begründung steht bei `ersteMoegliche()`. Herausgezogen ist es, weil es
 * seit dem Standbild **zwei** Aufrufer gibt: das Umrechnen und der Fang
 * eines Standbilds für ein Video, das gar nicht umgerechnet wird.
 */
async function leseWeg(
  quelle: Zerlegt,
): Promise<{ konfig: VideoDecoderConfig; hardware: 'prefer-hardware' | 'no-preference' } | undefined> {
  const konfig: VideoDecoderConfig = {
    codec: quelle.kodierung,
    codedWidth: quelle.breite,
    codedHeight: quelle.hoehe,
    description: quelle.beschreibung,
  }
  for (const hardware of ['prefer-hardware', 'no-preference'] as const) {
    const befund = await VideoDecoder.isConfigSupported({ ...konfig, hardwareAcceleration: hardware })
    if (befund.supported === true) return { konfig, hardware }
  }
  return undefined
}

/**
 * Ein Standbild fangen — **so gedreht, wie das Video angesehen wird.**
 *
 * Zwei Dinge stecken hier drin, und beide waren vorher falsch oder gar nicht
 * da:
 *
 * **Die Drehung.** Eine Hochkantaufnahme liegt in der Datei quer, und eine
 * Marke daneben sagt „beim Abspielen drehen". Der Schreiber gibt die Marke
 * weiter, der Player dreht — ein Standbild aber ist ein Bild und trägt keine
 * Marke. Hier stand die Leinwand in der **Bildgröße** statt in der
 * Ansichtsgröße; zu einem hochkant abspielenden Video gehörte damit ein
 * querformatiges Vorschaubild. Aufgefallen ist es nicht, weil die Kachel
 * beschneidet — sichtbar war nur der falsche Ausschnitt.
 *
 * **Der schwarze Anfang.** Die ersten Bilder einer Aufnahme sind oft schwarz,
 * weil die Kamera noch belichtet. Also weiter überschreiben, solange nur
 * Schwarz dasteht, höchstens über `STANDBILD_VERSUCHE` Bilder. Bleibt es
 * schwarz, gibt es kein Standbild: ein schwarzes Rechteck ist schlechter als
 * keins, dafür hat der Player einen Ausweg.
 */
function standbildfang(
  breite: number,
  hoehe: number,
  drehung: 0 | 90 | 180 | 270,
): {
  nimm(bild: CanvasImageSource): void
  sitzt(): boolean
  versuche(): number
  blob(): Promise<Blob | null>
} | null {
  const quer = drehung === 90 || drehung === 270
  const leinwand = new OffscreenCanvas(quer ? hoehe : breite, quer ? breite : hoehe)
  // **Hier `willReadFrequently`, und nur hier.** Diese Fläche wird bis zu
  // dreissig Mal mit `getImageData` zurückgelesen (`schwarz()`), und ein
  // Rücklesen von der Grafikkarte ist teuer. Auf der Rechenleinwand wäre
  // dieselbe Angabe schädlich: sie nimmt dort die Beschleunigung weg.
  const stift = leinwand.getContext('2d', { alpha: false, willReadFrequently: true })
  if (stift === null) return null

  let sitzt = false
  let versuche = 0
  return {
    nimm(bild) {
      if (sitzt || versuche >= STANDBILD_VERSUCHE) return
      versuche += 1
      stift.save()
      stift.translate(leinwand.width / 2, leinwand.height / 2)
      stift.rotate((drehung * Math.PI) / 180)
      stift.drawImage(bild, -breite / 2, -hoehe / 2, breite, hoehe)
      stift.restore()
      sitzt = !schwarz(stift, leinwand.width, leinwand.height)
    },
    sitzt: () => sitzt,
    versuche: () => versuche,
    blob: () => leinwand.convertToBlob({ type: 'image/jpeg', quality: 0.75 }),
  }
}

/**
 * Ein Standbild für ein Video, das **gar nicht umgerechnet wird**.
 *
 * Aus dem Betrieb, und es war die letzte Ecke, in der noch ein
 * `<video>`-Element stand: eine Aufnahme direkt aus der Kamera, 480x360 und
 * 2,3 MB, ist klein genug und schon H.264 — sie geht unverändert hinaus. Das
 * Standbild holte deshalb `grabPoster()`, also ein Element, und das meldete
 * auf dem iPhone nach zwanzig Sekunden „Dieses Video meldet seine Maße
 * nicht". Kein Vorschaubild.
 *
 * Der Kommentar daneben hatte es sogar ausgesprochen: „das sind Dateien unter
 * fünfundzwanzig Megabyte, dort hat es nie geklemmt." Eine Vermutung, und
 * eine Messung hat sie widerlegt.
 *
 * Hier wird dieselbe Maschinerie benutzt wie beim Umrechnen — Zerleger,
 * Dekodierer, Leinwand —, nur für **höchstens dreissig Bilder** statt für
 * alle. Bei einer Aufnahme, die schon klein genug ist, kostet das kaum etwas.
 */
async function standbildAusQuelle(
  quelle: Zerlegt,
  signal?: { abgebrochen: boolean },
): Promise<Blob | null> {
  const weg = await leseWeg(quelle)
  if (weg === undefined) {
    notiere(`Standbild fehlt: dieses Gerät kann ${quelle.kodierung} nicht lesen`)
    return null
  }
  const fang = standbildfang(quelle.breite, quelle.hoehe, quelle.drehung)
  if (fang === null) return null

  let fehlgeschlagen: Error | null = null
  const dekodierer = new VideoDecoder({
    output: (bild) => {
      try {
        fang.nimm(bild)
      } finally {
        bild.close()
      }
    },
    error: (fehler) => {
      fehlgeschlagen = fehler instanceof Error ? fehler : new Error(String(fehler))
    },
  })

  try {
    dekodierer.configure({ ...weg.konfig, hardwareAcceleration: weg.hardware })
    for (const stueck of quelle.bilder) {
      if (signal?.abgebrochen === true || fehlgeschlagen !== null) break
      if (fang.sitzt() || fang.versuche() >= STANDBILD_VERSUCHE) break
      dekodierer.decode(
        new EncodedVideoChunk({
          type: stueck.schluessel ? 'key' : 'delta',
          timestamp: stueck.zeit,
          duration: stueck.dauer,
          data: stueck.daten,
        }),
      )
      while (dekodierer.decodeQueueSize > 4) await neuerTakt()
    }
    await dekodierer.flush()
  } catch (fehler) {
    notiere(`Standbild fehlt: ${fehler instanceof Error ? fehler.message : String(fehler)}`)
    return null
  } finally {
    // Derselbe Grund wie beim Umrechnen: ein offener Dekodierer fehlt dem
    // naechsten Video, und genau deren Knappheit steht hinter
    // „Decoder failure".
    if (dekodierer.state !== 'closed') dekodierer.close()
  }

  if (!fang.sitzt()) {
    notiere(`Standbild fehlt: die ersten ${fang.versuche()} Bilder waren schwarz`)
    return null
  }
  return await fang.blob()
}

/** Einmal die Warteschlange leeren lassen. */
function neuerTakt(): Promise<void> {
  return new Promise((fertig) => setTimeout(fertig, 0))
}

/**
 * Die Datei zerlegen: Bildspur, Tonspur, Drehung.
 *
 * Angehängt wird in Stücken und nicht am Stück: `appendBuffer` mit
 * zweihundert Megabyte auf einmal legt ein Telefon lahm.
 */
async function zerlege(
  file: File,
  signal?: { abgebrochen: boolean },
  onGelesen?: (anteil: number) => void,
): Promise<Zerlegt> {
  const mp4box = await nachladen(() => import('mp4box'))
  // **`true` heisst: die Bilddaten bleiben liegen.** Die Vorgabe von `mp4box`
  // ist `false` -- der Leser wirft den Inhalt der `mdat`-Box weg, waehrend er
  // sie durchlaeuft. Das geht gut, solange die Verwaltungsdaten (`moov`) vorn
  // stehen: dann weiss er beim Durchlaufen schon, welche Spuren es gibt.
  //
  // **Eine Kamera schreibt `moov` aber hinten hin** -- beim Aufnehmen steht ja
  // noch nicht fest, wie lang es wird. Dann kommen erst die Bilder und dann
  // die Auskunft darueber, und alles vor der Auskunft war schon verworfen. Die
  // Folge war eine Meldung, die das Gegenteil behauptete: „In dieser Datei ist
  // keine Bildspur." Aus dem Betrieb, an einer HEVC-Aufnahme von 112 MB.
  //
  // **Aufgefallen ist es hier lange nicht**, weil die Pruefvideos im Browser
  // entstehen: `mp4-muxer` legt `moov` nach vorn, und kleine Dateien gehen
  // ohnehin in einem Stueck hinein. Erst beides zusammen -- hinten und in
  // Stuecken -- bringt den Fehler.
  //
  // **Der Preis ist Speicher**, und er gehoert genannt: der Leser haelt die
  // Bilddaten fest, bis alles durch ist. Es sind dieselben Bytes, die weiter
  // unten ohnehin in `bilder` liegen -- dieser Weg sammelt erst alles und
  // rechnet dann. Wer das eines Tages Bild fuer Bild durchreicht, wird beides
  // zugleich los.
  const datei = mp4box.createFile(true)

  const bilder: Bildstueck[] = []
  const toene: Bildstueck[] = []
  let info: MovieInfo | null = null

  await new Promise<void>((fertig, schiefgegangen) => {
    // **Was der Zerleger sagt, sagt keinem Menschen etwas.** Aus dem
    // Prüflauf: eine WebM-Aufnahme ergab die Meldung „ISOFile" -- der Name
    // einer Klasse in `mp4box`. Solange das Original danach trotzdem hochging,
    // stand sie nur klein an der Kachel; seit #41 ist sie der ganze Grund, aus
    // dem nichts passiert. Der Rohtext bleibt dahinter stehen, weil er beim
    // Suchen hilft -- aber davor steht ein Satz.
    datei.onError = (fehler: string) => schiefgegangen(new Error(
      `Diese Videodatei ließ sich nicht lesen (${fehler}).`,
    ))
    datei.onReady = (film: MovieInfo) => {
      info = film
      for (const spur of [...film.videoTracks.slice(0, 1), ...film.audioTracks.slice(0, 1)]) {
        datei.setExtractionOptions(spur.id, null, { nbSamples: 200 })
      }
      datei.start()
    }
    datei.onSamples = (id: number, _nutzer: unknown, proben: RohProbe[]) => {
      const istBild = info !== null && info.videoTracks.some((s) => s.id === id)
      const ziel = istBild ? bilder : toene
      for (const probe of proben) {
        // Ohne Daten ist eine Probe nur eine Ankuendigung -- das kommt bei
        // zerstueckelten Dateien vor und ist nichts, was hier weiterhilft.
        if (probe.data === undefined) continue
        ziel.push({
          daten: probe.data,
          schluessel: probe.is_sync,
          zeit: Math.round((probe.cts * 1_000_000) / probe.timescale),
          dauer: Math.round((probe.duration * 1_000_000) / probe.timescale),
        })
      }
      const letzte = proben[proben.length - 1]
      if (letzte !== undefined) datei.releaseUsedSamples(id, letzte.number)
    }

    void (async () => {
      try {
        const stueck = LESE_STUECK
        for (let anfang = 0; anfang < file.size; anfang += stueck) {
          if (signal?.abgebrochen) throw new Error(WORTLAUT.technik.video.umrechnenAbgebrochen)
          const ende = Math.min(anfang + stueck, file.size)
          const teil = await file.slice(anfang, ende).arrayBuffer()
          datei.appendBuffer(mp4box.MP4BoxBuffer.fromArrayBuffer(teil, anfang))
          onGelesen?.(ende / file.size)
        }
        datei.flush()
        fertig()
      } catch (fehler) {
        schiefgegangen(fehler)
      }
    })()
  })

  // **Drei verschiedene Fehler, drei verschiedene Saetze.** Hier stand
  // dreimal derselbe -- „In dieser Datei ist keine Bildspur." -- und aus einem
  // Bildschirmfoto aus dem Betrieb liess sich damit nicht sagen, woran es lag.
  const film = info as MovieInfo | null
  if (film === null) {
    throw new Error(WORTLAUT.technik.video.keineVerwaltungsdaten)
  }
  const bildspur = film.videoTracks[0]
  const tonspur = film.audioTracks[0]
  if (bildspur === undefined) throw new Error(WORTLAUT.technik.video.keineBildspur)

  return {
    breite: bildspur.video?.width ?? bildspur.track_width,
    hoehe: bildspur.video?.height ?? bildspur.track_height,
    drehung: ausMatrix(bildspur.matrix),
    kodierung: bildspur.codec,
    beschreibung: beschreibung(mp4box, datei as unknown as Mp4Datei, bildspur.id),
    zeitbasis: bildspur.timescale,
    // Die Dauer steht in der Spur, es braucht kein `<video>`-Element dafuer.
    dauerMs:
      bildspur.timescale > 0 ? Math.round((bildspur.duration / bildspur.timescale) * 1000) : 0,
    angekuendigt: bildspur.nb_samples,
    bilder,
    ton:
      tonspur === undefined || toene.length === 0
        ? null
        : {
            kodierung: tonspur.codec.startsWith('mp4a') ? 'aac' : 'opus',
            rate: tonspur.audio?.sample_rate ?? 48000,
            kanaele: tonspur.audio?.channel_count ?? 2,
            beschreibung: tonbeschreibung(mp4box, datei as unknown as Mp4Datei, tonspur.id),
            stuecke: toene,
          },
  }
}

/**
 * Die Drehung aus der Marke der Spur.
 *
 * **Eine iPhone-Aufnahme liegt in der Datei immer quer** und trägt die Drehung
 * als Matrix daneben. Wer sie beim Neuschreiben vergisst, bekommt ein Video,
 * das auf der Seite liegt — und merkt es erst auf dem Gerät. Die Zahlen stehen
 * als Festkomma mit 16 Nachkommastellen, `65536` ist also `1`.
 */
function ausMatrix(matrix: ArrayLike<number> | undefined): 0 | 90 | 180 | 270 {
  if (matrix === undefined || matrix.length < 5) return 0
  const zahl = (i: number): number => (matrix[i] ?? 0) / 65536
  const a = zahl(0)
  const b = zahl(1)
  const c = zahl(3)
  const d = zahl(4)

  if (Math.round(b) === 1 && Math.round(c) === -1) return 90
  if (Math.round(a) === -1 && Math.round(d) === -1) return 180
  if (Math.round(b) === -1 && Math.round(c) === 1) return 270
  return 0
}

/**
 * Die Beschreibung der Bildspur — `avcC` oder `hvcC` als rohe Bytes.
 *
 * Ohne sie weiß der Dekodierer nicht, wie die Stücke gebaut sind, und lehnt
 * jede Anfrage ab. Geschrieben wird die Box in einen Strom und der Kopf
 * (acht Bytes: Länge und Name) abgeschnitten — das ist genau das, was
 * WebCodecs als `description` erwartet.
 */
function beschreibung(mp4box: Mp4Modul, datei: Mp4Datei, spurId: number): Uint8Array | undefined {
  const eintrag = ersterEintrag(datei, spurId)
  const box = eintrag?.avcC ?? eintrag?.hvcC ?? eintrag?.vpcC ?? eintrag?.av1C
  return box === undefined ? undefined : ohneKopf(mp4box, box)
}

/**
 * Die Beschreibung der Tonspur — der `AudioSpecificConfig` aus `esds`.
 *
 * Der steckt in einer Kette von Deskriptoren: `ES_Descriptor` (Marke 3) trägt
 * einen `DecoderConfigDescriptor` (4), und der einen `DecoderSpecificInfo`
 * (5) — das sind die Bytes, die gebraucht werden. Die Kette wird über die
 * rohen Bytes abgelaufen statt über die Objekte der Bibliothek: das Format
 * steht in der Norm und ändert sich nicht, die Objekte einer Version schon.
 */
function tonbeschreibung(mp4box: Mp4Modul, datei: Mp4Datei, spurId: number): Uint8Array | undefined {
  const eintrag = ersterEintrag(datei, spurId)
  if (eintrag?.esds === undefined) return undefined
  const roh = ohneKopf(mp4box, eintrag.esds)
  if (roh === undefined) return undefined

  // Vier Bytes Version und Kennzeichen der Vollbox stehen vorn.
  let i = 4
  while (i < roh.length) {
    const marke = roh[i] ?? 0
    i += 1
    let laenge = 0
    for (let n = 0; n < 4; n += 1) {
      const byte = roh[i] ?? 0
      i += 1
      laenge = (laenge << 7) | (byte & 0x7f)
      if ((byte & 0x80) === 0) break
    }
    if (marke === 0x05) return roh.slice(i, i + laenge)
    // In 3 und 4 steckt die nächste Ebene; über deren Kopf hinweggehen.
    if (marke === 0x03) i += 3
    else if (marke === 0x04) i += 13
    else i += laenge
  }
  return undefined
}

function ersterEintrag(datei: Mp4Datei, spurId: number): Sampleeintrag | undefined {
  return datei.getTrackById(spurId)?.mdia?.minf?.stbl?.stsd?.entries?.[0]
}

/**
 * Eine Box als rohe Bytes, ohne ihren achtbyteigen Kopf.
 *
 * Die Bibliothek hat die Box beim Lesen in Felder zerlegt; zurueck zu den
 * Bytes kommt man nur, indem man sie wieder schreiben laesst. Die acht Bytes
 * am Anfang sind Laenge und Name -- genau die will WebCodecs nicht.
 */
function ohneKopf(mp4box: Mp4Modul, box: Box): Uint8Array | undefined {
  try {
    // Ohne Endian-Angabe: die Vorgabe ist BIG_ENDIAN, und so stehen die
    // Zahlen in einem MP4 auch.
    const strom = new mp4box.DataStream()
    box.write(strom)
    return new Uint8Array(strom.buffer.slice(8))
  } catch {
    return undefined
  }
}

/**
 * Ort und Zeit, bevor gerechnet wird.
 *
 * **Ein neu geschriebenes MP4 hat das `udta`-Atom nicht mehr**, in dem ein
 * iPhone seinen Aufnahmeort ablegt (`videoOrt.ts`). Also wird es vorher
 * gelesen und geht an den Server, genau wie die EXIF-Daten eines Fotos. Dort
 * ist es besser aufgehoben als in einer Datei, die jemand weiterreicht.
 */
export type { Aufnahme }

// --- Was von den beiden Paketen gebraucht wird ----------------------------
//
// Handgeschriebene Ausschnitte statt der mitgelieferten Typen: die
// Typdateien von `mp4box` beschreiben den ganzen Baum eines MP4 und ziehen
// beim Bauen mehr herein, als hier je angefasst wird.

interface RohProbe {
  data?: Uint8Array
  is_sync: boolean
  cts: number
  duration: number
  timescale: number
  number: number
}

interface Spur {
  id: number
  nb_samples: number
  codec: string
  timescale: number
  /** Laenge der Spur in Einheiten von `timescale`. */
  duration: number
  track_width: number
  track_height: number
  matrix?: ArrayLike<number>
  video?: { width: number; height: number }
  audio?: { sample_rate: number; channel_count: number }
}

interface MovieInfo {
  videoTracks: Spur[]
  audioTracks: Spur[]
}

interface Box {
  write(strom: { buffer: ArrayBuffer }): void
}

interface Mp4Modul {
  DataStream: new () => { buffer: ArrayBuffer }
}

interface Sampleeintrag {
  avcC?: Box
  hvcC?: Box
  vpcC?: Box
  av1C?: Box
  esds?: Box
}

interface Mp4Datei {
  getTrackById(id: number): {
    mdia?: { minf?: { stbl?: { stsd?: { entries?: Sampleeintrag[] } } } }
  } | undefined
}
